const STORAGE_KEY = 'rouggeShippingZone';

class RouggeShippingEstimator extends HTMLElement {
  static get observedAttributes() {
    return [
      'data-cart-total',
      'data-threshold',
      'data-rm-rate',
      'data-regions-rate',
      'data-near-threshold',
      'data-currency-code-enabled',
    ];
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[data-shipping-zone]');
        if (!button || !this.contains(button)) return;

        this.setZone(button.dataset.shippingZone);
      },
      { signal: this.abortController.signal }
    );

    window.addEventListener(
      'rougge:shipping-zone-change',
      (event) => {
        const nextZone = event.detail?.zone;
        if (nextZone === 'rm' || nextZone === 'regions') {
          this.zone = nextZone;
          this.scheduleRender();
        }
      },
      { signal: this.abortController.signal }
    );

    this.readConfiguration();
    this.zone = this.getStoredZone();
    this.scheduleRender();
  }

  disconnectedCallback() {
    this.abortController?.abort();

    if (this.renderFrame) {
      window.cancelAnimationFrame(this.renderFrame);
    }
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;

    this.readConfiguration();
    this.scheduleRender();
  }

  readConfiguration() {
    this.cartTotal = Number(this.dataset.cartTotal || 0);
    this.threshold = Number(this.dataset.threshold || 0);
    this.rmRate = Number(this.dataset.rmRate || 0);
    this.regionsRate = Number(this.dataset.regionsRate || 0);
    this.nearThreshold = Number(this.dataset.nearThreshold || 0);
    this.showCurrencyCode = this.dataset.currencyCodeEnabled === 'true';
  }

  getStoredZone() {
    try {
      const zone = window.sessionStorage.getItem(STORAGE_KEY);
      return zone === 'rm' || zone === 'regions' ? zone : null;
    } catch (error) {
      return null;
    }
  }

  setZone(zone) {
    if (zone !== 'rm' && zone !== 'regions') return;

    this.zone = zone;

    try {
      window.sessionStorage.setItem(STORAGE_KEY, zone);
    } catch (error) {
      // The estimator still works when storage is unavailable.
    }

    window.dispatchEvent(
      new CustomEvent('rougge:shipping-zone-change', {
        detail: { zone },
      })
    );
  }

  scheduleRender() {
    if (this.renderFrame) {
      window.cancelAnimationFrame(this.renderFrame);
    }

    this.renderFrame = window.requestAnimationFrame(() => {
      this.renderFrame = null;
      this.render();
    });
  }

  /**
   * Three states: far (> nearThreshold), near (<= nearThreshold) and unlocked.
   * Built with text nodes so only the formatted amount is ever inserted.
   */
  renderShippingMessage(element, qualifiesForFreeShipping, remaining) {
    if (qualifiesForFreeShipping) {
      element.replaceChildren('Envío gratis desbloqueado ✔');
      return;
    }

    const amount = document.createElement('strong');
    amount.textContent = this.formatMoney(remaining);

    if (remaining <= this.nearThreshold) {
      element.replaceChildren('¡Estás a ', amount, ' del envío gratis!');
    } else {
      element.replaceChildren(
        'Suma ',
        amount,
        ' más y el despacho va por nuestra cuenta.'
      );
    }
  }

  formatMoney(cents) {
    const formatted = new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0,
    }).format(cents / 100);

    return this.showCurrencyCode ? `${formatted} CLP` : formatted;
  }

  render() {
    const qualifiesForFreeShipping =
      this.threshold > 0 && this.cartTotal >= this.threshold;
    const hasSelectedZone = this.zone === 'rm' || this.zone === 'regions';

    const selectedRate =
      this.zone === 'regions'
        ? this.regionsRate
        : this.zone === 'rm'
          ? this.rmRate
          : null;

    const shippingCost = qualifiesForFreeShipping
      ? 0
      : hasSelectedZone
        ? selectedRate
        : null;
    const estimatedTotal = this.cartTotal + (shippingCost ?? 0);
    const remaining = Math.max(this.threshold - this.cartTotal, 0);
    const progress =
      this.threshold > 0
        ? Math.min((this.cartTotal / this.threshold) * 100, 100)
        : 0;

    this.querySelectorAll('[data-shipping-zone]').forEach((button) => {
      const active = button.dataset.shippingZone === this.zone;
      button.setAttribute('aria-checked', active ? 'true' : 'false');
      button.classList.toggle('is-active', active);
    });

    const shippingValue = this.querySelector(
      '[data-rougge-estimated-shipping]'
    );
    const estimatedTotalValue = this.querySelector(
      '[data-rougge-estimated-total]'
    );
    const estimatedTotalLabel = this.querySelector(
      '[data-rougge-estimated-total-label]'
    );
    const estimatedTotalHint = this.querySelector(
      '[data-rougge-estimated-total-hint]'
    );
    const shippingMessage = this.querySelector(
      '[data-rougge-shipping-message]'
    );
    const progressBar = this.querySelector(
      '[data-rougge-shipping-progress]'
    );

    if (shippingValue) {
      shippingValue.textContent = qualifiesForFreeShipping
        ? 'GRATIS'
        : hasSelectedZone
          ? this.formatMoney(shippingCost)
          : `Desde ${this.formatMoney(Math.min(this.rmRate, this.regionsRate))}`;
    }

    if (estimatedTotalValue) {
      estimatedTotalValue.textContent = this.formatMoney(estimatedTotal);
    }

    if (estimatedTotalLabel) {
      estimatedTotalLabel.textContent =
        qualifiesForFreeShipping || hasSelectedZone ? 'Total estimado' : 'Subtotal';
    }

    if (estimatedTotalHint) {
      estimatedTotalHint.textContent = qualifiesForFreeShipping
        ? 'con despacho gratis'
        : hasSelectedZone
          ? 'con despacho estimado'
          : 'despacho en checkout';
    }

    if (shippingMessage) {
      this.renderShippingMessage(
        shippingMessage,
        qualifiesForFreeShipping,
        remaining
      );
    }

    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }
  }
}

if (!customElements.get('rougge-shipping-estimator')) {
  customElements.define(
    'rougge-shipping-estimator',
    RouggeShippingEstimator
  );
}
